import { Injectable } from "@angular/core";
import { UserDataModel } from "@modules/auth";

declare const pendo: any;

@Injectable({
  providedIn: "root",
})
export class PendoService {
  private initialized = false;
  private readonly accountId = "liv2bme-app";
  private readonly accountName = "Liv2BMe Social Media Platform";

  constructor() {}

  initialize(): void {
    if (this.initialized || typeof pendo === "undefined") {
      return;
    }

    pendo.initialize({
      visitor: {
        id: "anonymous",
      },
      account: {
        id: this.accountId,
        name: this.accountName,
      },
    });

    this.initialized = true;
    console.debug("[Pendo] Initialized as anonymous");
  }

  identifyUser(user: UserDataModel): void {
    if (typeof pendo === "undefined") {
      console.warn("Pendo is not loaded");
      return;
    }

    const visitorId = this.getVisitorId(user);
    if (!visitorId) {
      console.warn("[Pendo] Missing visitor id, identify skipped");
      return;
    }

    this.initialize();

    const visitorData = {
      id: visitorId,
      email: user.email,
      full_name: this.getFullName(user),
      role: this.getUserRole(user),
      grade: user.grade,
      state: user.state,
      survey_completed: user.isSurveyTaken,
    };

    const accountData = {
      id: this.accountId,
      name: this.accountName,
    };

    pendo.identify({ visitor: visitorData, account: accountData });
    pendo.pageLoad();
    pendo.track("mobile_user_identified", {
      visitorId,
      platform: "mobile-webview",
      role: visitorData.role,
    });
  }

  logout(): void {
    if (typeof pendo === "undefined") {
      return;
    }

    pendo.identify({
      visitor: { id: "anonymous" },
      account: { id: this.accountId },
    });
  }

  pageLoad(url?: string): void {
    if (typeof pendo === "undefined") {
      return;
    }

    this.initialize();
    pendo.pageLoad({
      location: {
        href: url ? `${window.location.origin}${url}` : window.location.href,
      },
    });
  }

  trackEvent(eventName: string, metadata?: any): void {
    if (typeof pendo === "undefined") {
      return;
    }

    this.initialize();
    pendo.track(eventName, metadata);
  }

  private getFullName(user: UserDataModel): string {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    return `${firstName} ${lastName}`.trim() || user.userName || "Unknown User";
  }

  private getUserRole(user: UserDataModel): string {
    if (user.roles && user.roles.length > 0) {
      if (
        user.roles.includes("SuperAdmin") ||
        user.roles.includes("Admin") ||
        user.roles.includes("Moderator")
      ) {
        return "admin";
      }
      return "basic";
    }
    return "basic";
  }

  private getVisitorId(user: UserDataModel): string | null {
    const rawId = user?.id ?? user?.userId;
    if (rawId === undefined || rawId === null || rawId === "") {
      return null;
    }

    return String(rawId);
  }
}
